import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { X, ChevronRight, ChevronLeft, Briefcase, Clock, Bell, Download, Crown } from 'lucide-react';

const TUTORIAL_SLIDES = [
    {
        id: 1,
        icon: Briefcase,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        title: 'Add Your First Case',
        description: 'Start by creating a case for each matter you\'re working on. Tap the + button at the bottom of the screen to add a new case.',
        tips: [
            'Enter the case name and client details',
            'Choose a billing type (hourly, flat fee, or retainer)',
            'Set your hourly rate for accurate billing'
        ]
    },
    {
        id: 2,
        icon: Clock,
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        title: 'Track Your Time',
        description: 'Log time entries for each case to track billable hours. Open a case and use the timer or add time manually.',
        tips: [
            'Use the timer for real-time tracking',
            'Add manual entries for past work',
            'Include notes to describe the work done'
        ]
    },
    {
        id: 3,
        icon: Bell,
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        title: 'Never Miss a Deadline',
        description: 'Add deadlines and reminders to each case. Enable push notifications in Settings to get alerts before due dates.',
        tips: [
            'Mark critical deadlines for priority alerts',
            'Get reminders 7, 3, and 1 day before',
            'View all deadlines in the Calendar tab'
        ]
    },
    {
        id: 4,
        icon: Download,
        iconBg: 'bg-violet-100',
        iconColor: 'text-violet-600',
        title: 'Backup Your Data',
        description: 'Keep your data safe! Go to Settings to download backups. We recommend downloading a backup daily.',
        tips: [
            'Export to Excel for spreadsheet access',
            'Download PDF reports',
            'Enable daily backup reminders at 5:30 PM'
        ]
    },
    {
        id: 5,
        icon: Crown,
        iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100',
        iconColor: 'text-amber-500',
        title: 'Free vs Pro',
        description: 'Start free with 1 case forever, or upgrade to Pro for unlimited cases at just $28/year.',
        tips: [
            'Free: 1 case, all features, forever',
            'Pro ($28/year): Unlimited cases',
            'Upgrade anytime in Settings'
        ]
    }
];

export default function OnboardingTutorial({ onComplete }) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    const handleNext = () => {
        if (currentSlide < TUTORIAL_SLIDES.length - 1) {
            setCurrentSlide(currentSlide + 1);
        } else {
            handleComplete();
        }
    };

    const handlePrev = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleComplete = () => {
        localStorage.setItem('voilaw_onboarding_complete', 'true');
        setIsVisible(false);
        if (onComplete) onComplete();
    };

    const handleSkip = () => {
        handleComplete();
    };

    if (!isVisible) return null;

    const slide = TUTORIAL_SLIDES[currentSlide];
    const SlideIcon = slide.icon;
    const isLastSlide = currentSlide === TUTORIAL_SLIDES.length - 1;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 pb-24">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[calc(100vh-120px)] flex flex-col overflow-hidden animate-fade-in-up">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b shrink-0">
                    <div className="flex items-center gap-2">
                        {TUTORIAL_SLIDES.map((_, index) => (
                            <div
                                key={index}
                                className={`h-2 rounded-full transition-all ${
                                    index === currentSlide 
                                        ? 'w-6 bg-[#1C6EE7]' 
                                        : index < currentSlide 
                                            ? 'w-2 bg-[#1C6EE7]/50' 
                                            : 'w-2 bg-slate-200'
                                }`}
                            />
                        ))}
                    </div>
                    <button
                        onClick={handleSkip}
                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="tutorial-skip-btn"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 text-center">
                    {/* Icon */}
                    <div className={`${slide.iconBg} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6`}>
                        <SlideIcon className={`h-10 w-10 ${slide.iconColor}`} />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-slate-900 mb-3">
                        {slide.title}
                    </h2>

                    {/* Description */}
                    <p className="text-slate-600 mb-6">
                        {slide.description}
                    </p>

                    {/* Tips */}
                    <div className="bg-slate-50 rounded-2xl p-4 text-left">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Quick Tips</p>
                        <ul className="space-y-2">
                            {slide.tips.map((tip, index) => (
                                <li key={index} className="flex items-start gap-2 text-sm text-slate-700">
                                    <span className="text-[#1C6EE7] mt-0.5">•</span>
                                    {tip}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t flex items-center justify-between shrink-0 bg-white">
                    <Button
                        variant="ghost"
                        onClick={handlePrev}
                        disabled={currentSlide === 0}
                        className="rounded-full"
                        data-testid="tutorial-prev-btn"
                    >
                        <ChevronLeft className="h-5 w-5 mr-1" />
                        Back
                    </Button>

                    <span className="text-sm text-slate-500">
                        {currentSlide + 1} of {TUTORIAL_SLIDES.length}
                    </span>

                    <Button
                        onClick={handleNext}
                        className="rounded-full bg-[#1C6EE7] hover:bg-[#1559c7]"
                        data-testid="tutorial-next-btn"
                    >
                        {isLastSlide ? (
                            <>Get Started</>
                        ) : (
                            <>
                                Next
                                <ChevronRight className="h-5 w-5 ml-1" />
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
